/**
 * LocationPanel — show aerial + street view for the property's address.
 *
 * Hidden entirely if the backend reports Maps isn't configured (admin
 * hasn't set GOOGLE_MAPS_API_KEY yet). Otherwise: shows current images
 * if cached, or a "Look up location" button to geocode and fetch them.
 */

import { useEffect, useState, useCallback } from "react";
import { MapPin, RefreshCw, AlertTriangle } from "lucide-react";
import {
  fetchLocation,
  lookupLocation,
  type PropertyLocation,
} from "../../api/client";
import { useToast } from "../../contexts/ToastContext";
import { friendlyError } from "../../utils/errors";

interface LocationPanelProps {
  dealId: string;
  address: string | null | undefined;
}

export default function LocationPanel({ dealId, address }: LocationPanelProps) {
  const toast = useToast();
  const [configured, setConfigured] = useState<boolean | null>(null);
  const [location, setLocation] = useState<PropertyLocation | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const status = await fetchLocation(dealId);
      setConfigured(status.configured);
      setLocation(status.location);
    } catch {
      setConfigured(false);
    }
  }, [dealId]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleLookup() {
    const trimmed = (address || "").trim();
    if (!trimmed) {
      toast.error("Add a property address first.");
      return;
    }
    setLoading(true);
    try {
      const loc = await lookupLocation(dealId, trimmed);
      setLocation(loc);
      toast.success("Location updated");
    } catch (e: any) {
      toast.error(
        friendlyError(
          e?.response?.data?.detail ||
            "Couldn't look up that address. Check the spelling and try again.",
        ),
      );
    } finally {
      setLoading(false);
    }
  }

  // Hide the whole panel if Maps isn't configured — no point showing a
  // dead button to brokers.
  if (configured === false) return null;
  if (configured === null) return null; // initial flash

  const hasImages =
    location && (location.static_map_url || location.street_view_url);

  return (
    <div className="bg-white rounded-xl border border-grove-300 shadow-sm p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg font-semibold text-bark-800 flex items-center gap-2">
          <MapPin className="w-4 h-4 text-forest-700" />
          Location
        </h3>
        <button
          onClick={handleLookup}
          disabled={loading || !(address || "").trim()}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border border-grove-300 rounded-lg text-bark-700 hover:bg-grove-50 disabled:opacity-50 transition-all"
        >
          {loading ? (
            <RefreshCw className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <RefreshCw className="w-3.5 h-3.5" />
          )}
          {hasImages ? "Refresh" : "Look up"}
        </button>
      </div>

      {!(address || "").trim() && (
        <div className="flex items-start gap-2 text-sm text-bark-500">
          <AlertTriangle className="w-4 h-4 text-honey-600 mt-0.5 shrink-0" />
          Add a street address on this property and we'll pull the aerial and
          street view.
        </div>
      )}

      {location?.formatted_address && (
        <p className="text-sm text-bark-500">
          {location.formatted_address}
        </p>
      )}

      {hasImages && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {location?.static_map_url && (
            <figure className="space-y-1.5">
              <img
                src={location.static_map_url}
                alt="Aerial view of the property"
                className="w-full h-auto rounded-lg border border-grove-300"
                loading="lazy"
              />
              <figcaption className="text-[11px] text-bark-400 uppercase tracking-wider">
                Aerial
              </figcaption>
            </figure>
          )}
          {location?.street_view_url && (
            <figure className="space-y-1.5">
              <img
                src={location.street_view_url}
                alt="Street view of the property"
                className="w-full h-auto rounded-lg border border-grove-300"
                loading="lazy"
              />
              <figcaption className="text-[11px] text-bark-400 uppercase tracking-wider">
                Street view
              </figcaption>
            </figure>
          )}
        </div>
      )}
    </div>
  );
}
